<template>
  <SessionSlide
    ref="slide"
    :id="'slide' + props.index"
    visual-position="fixed"
    :class="{
      'slide-after-activity': !isDisplayed,
      'submitted-mapping': isDisplayed,
    }"
    :data-test="`session-slide-${index}`"
  >
    <template #text>
      <SlideText :text="prismicSlice.primary.text_body" />
      <CmsDropdown
        v-if="dropdownId && isLoaded"
        :answers="dropdownAnswers"
        :questionnaire="questionnaire"
        :dropdown-id="dropdownId"
        :is-shuffle="!!prismicSlice.primary.dropdown_shuffle"
      />
    </template>
    <template #visual>
      <VisualPositioningCanvas v-if="!!image">
        <CmsImg ref="visual" class="slide-image" :src="image" />
      </VisualPositioningCanvas>
    </template>
  </SessionSlide>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import SessionSlide from "../../sessions/session-slide.vue";
import VisualPositioningCanvas from "../../sessions/visual-positioning-canvas.vue";
import CmsImg from "../cms-img.vue";
import CmsDropdown, { AnswerData } from "../cms-dropdown.vue";
import { CmsSessionSlideWithDropdown } from "../../../services/cms/cms-type-definitions";
import {
  SessionDetails,
  SessionQuestionnaireDetails,
  useSessionQuestionnaire,
} from "../../../composables/useSessionQuestionnaire";
import cms from "../../../services/cms/cms";
import { PrismicSliceWithData } from "../../../views/cms/cms-session.vue";
import { useCmsSessionStore } from "../../../stores/useCmsSessionStore";
import pinia from "../../../stores";
import SlideText from "./slide-text.vue";
import { useToggle } from "../../../composables/useToggle";

const props = defineProps<{
  slice: PrismicSliceWithData;
  index: number;
  sessionDetails: SessionDetails;
}>();

const cmsSessionStore = useCmsSessionStore(pinia());

const { state: isDisplayed, toggleState: toggleIsDisplayed } = useToggle(false);

const prismicSlice = computed<CmsSessionSlideWithDropdown>(() => {
  return props.slice.prismicSlice as CmsSessionSlideWithDropdown;
});

const sessionQuestionnaireDetails = computed<SessionQuestionnaireDetails>(() => {
  return {
    sliceType: "cms-session-slide-with-dropdown",
    ...props.sessionDetails,
  };
});

const { questionnaire, isLoaded } = useSessionQuestionnaire(sessionQuestionnaireDetails.value);

const visual = ref<InstanceType<typeof CmsImg> | null>(null);
const slide = ref<InstanceType<typeof SessionSlide> | null>(null);
const visualDiv = computed(() => slide?.value?.visualDiv);

const image = computed(() => prismicSlice.value.primary.image);

const dropdownId = computed<string>(() => {
  return cms.asText(prismicSlice.value.primary.dropdown_id);
});

const dropdownAnswers = computed<AnswerData[]>(() => {
  return prismicSlice.value.items.map((e) => {
    return {
      answerLabel: cms.asText(e.dropdown_answer_label),
      answerId: cms.asText(e.dropdown_answer_id),
    };
  }) as AnswerData[];
});

watchEffect(() => {
  if (!isDisplayed.value && cmsSessionStore.sectionsToDisplay.includes(props.slice.section)) {
    toggleIsDisplayed();
  }
});

defineExpose({
  visualDiv,
  visual,
  slide,
});
</script>
